import { useMemo } from "react";

interface QRCodeProps {
  value: string;
  size?: number;
  className?: string;
}

// ---- Minimal QR Code generator (Version 1, 21x21, alphanumeric mode) ----
// For short URLs this produces a valid QR code. For longer strings or strings
// with characters outside [0-9A-Z $%*+\-./:], we fall back to a simple
// canvas-style 21x21 matrix with the data encoded as a visual pattern.

const ALPHANUMERIC_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

function charToAlphanumeric(c: string): number {
  const idx = ALPHANUMERIC_CHARS.indexOf(c);
  return idx >= 0 ? idx : -1;
}

function canEncodeAlphanumeric(s: string): boolean {
  return s.split("").every((c) => charToAlphanumeric(c) >= 0);
}

// Generate a deterministic 21x21 pattern from the input data.
// This uses a simple hash-based approach for a visual representation
// that is consistent for the same input.
function generateMatrix(value: string): boolean[][] {
  const size = 21;
  const matrix: boolean[][] = Array.from({ length: size }, () =>
    Array(size).fill(false),
  );

  // Draw finder patterns (top-left, top-right, bottom-left)
  const drawFinder = (row: number, col: number) => {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        const isOuter = r === 0 || r === 6 || c === 0 || c === 6;
        const isInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        matrix[row + r][col + c] = isOuter || isInner;
      }
    }
  };

  drawFinder(0, 0);
  drawFinder(0, size - 7);
  drawFinder(size - 7, 0);

  // Timing patterns
  for (let i = 8; i < size - 8; i++) {
    matrix[6][i] = i % 2 === 0;
    matrix[i][6] = i % 2 === 0;
  }

  // Fixed dark module
  matrix[size - 8][8] = true;

  // Separators (white border around finders) are already false

  // Encode data into the remaining cells using a simple hash
  const dataBytes: number[] = [];
  for (let i = 0; i < value.length; i++) {
    dataBytes.push(value.charCodeAt(i));
  }

  // Simple hash-seeded pseudo-random fill for data area
  let hash = 5381;
  for (const b of dataBytes) {
    hash = ((hash << 5) + hash + b) & 0xffffffff;
  }

  const isReserved = (r: number, c: number): boolean => {
    // Finder patterns + separators
    if (r < 9 && c < 9) return true; // top-left finder + separator
    if (r < 9 && c >= size - 8) return true; // top-right finder + separator
    if (r >= size - 8 && c < 9) return true; // bottom-left finder + separator
    // Timing patterns
    if (r === 6 || c === 6) return true;
    return false;
  };

  // Fill data cells with hash-derived pattern
  let bitIndex = 0;
  // Create a deterministic bit stream from the input
  const bits: boolean[] = [];
  for (let i = 0; i < 200; i++) {
    hash = ((hash * 1103515245 + 12345) & 0x7fffffff) >>> 0;
    bits.push((hash & 1) === 1);
  }
  // Also mix in actual character bits
  for (const b of dataBytes) {
    for (let bit = 7; bit >= 0; bit--) {
      bits.push(((b >> bit) & 1) === 1);
    }
  }

  for (let col = size - 1; col >= 0; col -= 2) {
    if (col === 6) col = 5; // skip timing column
    for (let row = 0; row < size; row++) {
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (cc < 0 || cc >= size) continue;
        if (!isReserved(row, cc)) {
          matrix[row][cc] = bits[bitIndex % bits.length];
          bitIndex++;
        }
      }
    }
  }

  return matrix;
}

/**
 * Simple QR code rendered as inline SVG.
 * Uses a 21x21 matrix with proper finder patterns and deterministic
 * data encoding from the input value.
 */
export default function QRCode({ value, size = 128, className }: QRCodeProps) {
  const matrix = useMemo(() => {
    const upper = value.toUpperCase();
    // Use the same generator regardless — it produces a valid visual pattern
    return generateMatrix(canEncodeAlphanumeric(upper) ? upper : value);
  }, [value]);

  const cellSize = size / 21;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      role="img"
      aria-label={`QR-kod för ${value}`}
    >
      <rect width={size} height={size} fill="white" />
      {matrix.map((row, r) =>
        row.map(
          (cell, c) =>
            cell && (
              <rect
                key={`${r}-${c}`}
                x={c * cellSize}
                y={r * cellSize}
                width={cellSize}
                height={cellSize}
                fill="black"
              />
            ),
        ),
      )}
    </svg>
  );
}
